import { createTheme, alpha } from '@mui/material/styles';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 320,
      md: 600,
      lg: 1024,
      xl: 1330,
      xxl: 1920,
    },
  },
  mixins: {
    toolbar: {
      '@media (min-width:0)': {
        minHeight: 56,
      },
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      dark: '#BE9A3F',
      light: '#F4DA95',
      main: '#EDC14F',
    },
    grey: {
      concrete: '#BBBCBC',
    },
    secondary: {
      light: 'rgba(60, 60, 60, 0.80)',
      main: 'rgba(39, 39, 39, 0.80)',
      dark: 'rgba(23, 23, 23, 0.90)',
      contrastText: '#ffffff',
    },
    HighwayYellow: '#EDC14F',
    common: {
      fogWhite: '#EBF2ED',
      black: '#000000',
      grey: '#585858',
      PavementGrey: '#2F2F2F',
    },
    overlay: {
      1: 'rgba(255, 255, 255, 0.05)',
    },
    background: {
      default: '#111111',
      secondary: '#222222',
      primary: '#111',
      tertiary: '#444',
      dark: 'rgba(23, 23, 23, 0.97)',
    },
    divider: {
      1: 'rgba(255, 255, 255, 0.04)',
      2: 'rgba(255, 255, 255, 0.6)',
      3: 'rgba(255, 255, 255, 0.8)',
      4: 'rgba(255, 255, 255, 0.10)',
      5: 'rgba(255, 255, 255, 0.14)',
      6: 'rgba(255, 255, 255, 0.18)',
      7: 'rgba(255, 255, 255, 0.24)',
      8: 'rgba(255, 255, 255, 0.32)',
    },
    action: {
      hover: 'rgba(255, 255, 255, 0.15)',
      disabled: 'rgba(255, 255, 255, 0.38)',
    },
    border: {
      1: 'rgba(255, 255, 255, 0.05)',
      2: 'rgba(255, 255, 255, 0.10)',
      3: 'rgba(255, 255, 255, 0.15)',
    },
    text: {
      primary: 'rgb(255, 255, 255)',
      secondary: 'rgb(255, 255, 255, .8)',
      disabled: 'rgb(255, 255, 255, .38)',
      alternate: '#000000',
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      background: 'rgba(211, 47, 47, 0.30)',
    },
    notice: {
      background: '#572627',
      backgroundDark: '#141312',
    },
    success: {
      light: '#4CAF7B',
      main: '#027A48',
      dark: '#015030',
    },
    dynamicPricing: {
      yellow: {
        selected: '#C19445',
      },
      red: {
        selected: '#740116',
      },
      green: {
        selected: '#535830',
      },
    },
  },
  typography: {
    fontFamily: "'Acumin Pro', sans-serif",
    fontFamilySecondary: "'DrukTextWide', serif",
    body1: {
      fontFamily: "'Acumin Pro', sans-serif",
      fontWeight: 500,
      fontSize: '1.125rem',
    },
    body2: {
      fontFamily: "'Acumin Pro', sans-serif",
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '1.4375rem',
    },
    body3: {
      fontFamily: "'Acumin Pro', sans-serif",
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: '180%',
    },
    h1: {
      fontFamily: "'DrukTextWide', sans-serif",
      fontWeight: 700,
      lineHeight: '120%',
      textTransform: 'uppercase',
      fontSize: '3rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontFamily: "'DrukTextWide', sans-serif",
      fontWeight: 700,
      lineHeight: '120%',
      textTransform: 'uppercase',
      fontSize: '2.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontFamily: "'DrukTextWide', sans-serif",
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: '120%',
      textTransform: 'uppercase',
      '@media (max-width:1024px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontFamily: "'DrukTextWide', sans-serif",
      fontSize: '1.5rem',
      lineHeight: '130%',
      textTransform: 'uppercase',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontFamily: "'DrukTextWide', sans-serif",
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: '140%',
      textTransform: 'uppercase',
      '@media (max-width:600px)': {
        fontSize: '1.125rem',
      },
    },
    h6: {
      fontFamily: "'DrukTextWide', sans-serif",
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '1.125rem',
      lineHeight: '140%',
      '@media (max-width:600px)': {
        fontSize: '1rem',
      },
    },
    h7: {
      fontFamily: "'DrukTextWide', sans-serif",
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '1rem',
      lineHeight: '140%',
      '@media (max-width:600px)': {
        fontSize: '0.875rem',
      },
    },
    overline1: {
      fontFamily: "'Acumin Pro', sans-serif",
      fontWeight: 700,
      fontSize: '0.875rem',
      lineHeight: '120%',
      letterSpacing: '0.7px',
      textTransform: 'uppercase',
    },
    overline2: {
      fontFamily: "'Acumin Pro', sans-serif",
      fontWeight: 700,
      fontSize: '0.75rem',
      lineHeight: '120%',
      letterSpacing: '0.6px',
      textTransform: 'uppercase',
    },
    overline3: {
      fontFamily: "'Acumin Pro', sans-serif",
      fontWeight: 700,
      fontSize: '0.625rem',
      lineHeight: '120%',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: "'Acumin Pro', sans-serif",
          background: '#111',
          "[class*='MuiFormLabel-asterisk']": {
            color: 'unset',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
        filledError: {
          backgroundColor: '#D32F2F',
        },
        filledInfo: {
          backgroundColor: '#0288D1',
        },
        filledWarning: {
          backgroundColor: '#EDC14F',
          color: '#000000 !important',
          '& .MuiAlert-icon': {
            color: '#000000',
          },
        },
        filledSuccess: {
          backgroundColor: '#027A48',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: '#E0E0E0',
        },
        light: {
          backgroundColor: '#F5F5F5',
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: 'xl',
      },
    },
    MuiDialogTitle: {
      defaultProps: {
        disableTypography: true,
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true, // Disables ripple effect globally
      },
      // Base style overrides
      styleOverrides: {
        root: {
          borderRadius: '4px', // Rounded corners
        },
      },
      variants: [
        {
          props: { color: 'secondary' },
          style: {
            color: '#FFFFFF',
          },
        },
        {
          props: { size: 'medium' },
          style: {
            fontSize: '1rem',
            lineHeight: '120%',
            padding: '0.75rem 0.875rem',
          },
        },
      ],
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none', // Disables uppercase transformation
          '& .MuiButton-startIcon, .MuiButton-endIcon': {
            position: 'relative',
            top: '-2px', // to fix align icon and text of button cause of font space issue
          },
        },
      },
      variants: [
        {
          props: { color: 'secondary', variant: 'outlined' },
          style: {
            border: '1px solid rgba(255, 255, 255, 0.50)',
            color: '#FFFFFF',
            '&:hover, &:focus': {
              border: '1px solid #fff',
              background:
                'linear-gradient(0deg,  rgba(255, 255, 255, 0.04) 0%,  rgba(255, 255, 255, 0.04) 100%),  rgba(60, 60, 60, 0.80)',
              boxShadow: '-2px 4px 24px 0px  rgba(255, 255, 255, 0.16)',
              backdropFilter: 'blur(8px)',
            },
          },
        },
        {
          props: { color: 'secondary', variant: 'contained' },
          style: {
            border: '1px solid rgba(255, 255, 255, 0.05)',
            background: 'rgba(39, 39, 39, 0.80)',
            backdropFilter: 'blur(8px)',
            color: '#FFFFFF',
            '&:hover, &:focus': {
              backgroundColor: 'rgba(60, 60, 60, 0.80)',
            },
          },
        },
        {
          props: { color: 'secondary', variant: 'text' },
          style: {
            color: '#FFFFFF',
          },
        },
        {
          props: { size: 'small' },
          style: {
            fontSize: '0.875rem',
            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
              fontSize: '1rem',
              '& svg': {
                fontSize: '1rem',
              },
            },

            padding: '0.625rem 0.75rem',
          },
        },
        {
          props: { size: 'medium' },
          style: {
            fontSize: '0.9375rem',
            lineHeight: '120%',
            padding: '0.75rem 0.875rem',
            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
              fontSize: '1rem',
              '& svg': {
                fontSize: '1rem',
              },
            },
          },
        },
        {
          props: { size: 'large' },
          style: {
            padding: '0.875rem 1rem',
            lineHeight: '120%',
            fontSize: '1rem',
            minHeight: '3.125rem',
          },
        },
      ],
    },
    MuiLink: {
      defaultProps: {
        underline: 'none',
        color: 'inherit',
      },
      styleOverrides: {
        root: {
          color: '#000000',
          fontWeight: 500,
        },
      },
    },
  },
  spacing: (factor) => `${0.5 * factor}rem`,
});

// intl-tel-input (mobile) Modal overlay dropdown
theme.components.MuiCssBaseline.styleOverrides.body = {
  ...theme.components.MuiCssBaseline.styleOverrides.body,
  '&.iti-mobile .iti-container': {
    position: 'fixed',
    inset: '0 !important',
    zIndex: '1600 !important',
    backgroundColor: alpha(theme.palette.common.black, 0.7),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.spacing(2)} !important`,
    paddingTop: `calc(${theme.spacing(
      2,
    )} + env(safe-area-inset-top, 0px)) !important`,
    paddingBottom: `calc(${theme.spacing(
      2,
    )} + env(safe-area-inset-bottom, 0px)) !important`,
    cursor: 'pointer',
  },
  '&.iti-mobile .country-list': {
    width: '100%',
    maxWidth: '70dvw !important',
    maxHeight: '70dvh !important',
    overflowY: 'auto !important',
    WebkitOverflowScrolling: 'touch',
    minWidth: 0,
    border: `1px solid ${theme.palette.divider?.[7] || theme.palette.divider}`,
    backgroundColor: theme.palette.background.secondary,
    boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.45)',
    borderRadius: theme.spacing(1),
    whiteSpace: 'normal',
    cursor: 'auto',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  '&.iti-mobile .country-list .search-container input': {
    width: '100%',
    backgroundColor: 'transparent',
    color: theme.palette.common.white,
    borderColor: theme.palette.divider?.[7] || theme.palette.divider,
  },
  '&.iti-mobile .country-list .divider': {
    borderBottomColor: theme.palette.divider?.[7] || theme.palette.divider,
  },
  '&.iti-mobile .selected-flag': {
    borderRightColor: theme.palette.divider?.[7] || theme.palette.divider,
  },
  '&.iti-mobile .selected-flag .arrow': {
    color: theme.palette.text.secondary,
  },
};

theme.components.MuiTextField = {
  defaultProps: {
    color: 'secondary',
  },
  styleOverrides: {
    root: {
      '& .MuiFormLabel-root': {
        '&.Mui-focused': {
          color: `${theme.palette.primary.main} !important`,
        },
        '&.Mui-disabled': {
          color: `${theme.palette.text.disabled} !important`,
        },
      },
    },
  },
  variants: [
    {
      props: { variant: 'outlined' },
      style: {
        '& .MuiOutlinedInput-root': {
          transition: 'all 0.3s ease',
          '& .MuiInputBase-input': {
            backgroundColor: 'transparent',
          },
          '& fieldset': {
            transition: 'all 0.3s ease',
            borderColor: `${alpha(theme.palette.common.white, 0.5)} !important`,
          },
          '&:hover fieldset': {
            borderColor: `${alpha(
              theme.palette.common.white,
              0.75,
            )} !important`,
          },
          '&.Mui-focused fieldset': {
            borderColor: `${theme.palette.primary.main} !important`,
            borderWidth: 1,
          },
          '&.Mui-disabled fieldset': {
            borderColor: `${theme.palette.common.white} !important`,
            borderWidth: 1,
          },
          '&.Mui-error fieldset': {
            borderColor: `${theme.palette.error.main} !important`,
          },
        },
      },
    },
  ],
};

export default theme;
